import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";

const TIER_COLORS = {
  1: "#e8f5e9", // green - same bldg 12mo
  2: "#e3f2fd", // blue - zone 12mo
  3: "#fff8e1", // yellow - same bldg 12-24mo
  4: "#fce4ec", // pink - zone 12-24mo
};

const TIER_LABELS = {
  1: "Same Bldg (0-12mo)",
  2: "Same Bldg (12-24mo)",
  3: "Zone+Tier (0-12mo)",
  4: "Zone+Tier (12-24mo)",
};

export default function CompScoringPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiPrediction, setAiPrediction] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [targetScore, setTargetScore] = useState(700);

  const loadComps = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await authFetch(`/comp-scoring/${id}`);
      if (result.error) {
        setError(result.error);
      } else {
        setData(result);
        if (result.target_score) setTargetScore(result.target_score);
        // Load cached AI prediction if available
        if (result.ai_prediction) {
          setAiPrediction(result.ai_prediction);
        }
      }
    } catch (e) {
      setError(e.toString());
    }
    setLoading(false);
  };

  const refreshComps = async () => {
    setRefreshing(true);
    try {
      const result = await authFetch(`/comp-scoring/${id}/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_score: targetScore }),
      });
      if (result.error) {
        setError(result.error);
      } else {
        setData(result);
        setAiPrediction(null); // clear old AI prediction since comps changed
      }
    } catch (e) {
      setError(e.toString());
    }
    setRefreshing(false);
  };

  const toggleExclude = async (compListingId, currentlyExcluded) => {
    try {
      const result = await authFetch(`/comp-scoring/${id}/exclude`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comp_listing_id: compListingId, excluded: !currentlyExcluded }),
      });
      if (!result.error) {
        setData(result);
      }
    } catch (e) {
      console.error("Exclude error:", e);
    }
  };

  const runAiPrediction = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await authFetch(`/comp-scoring/${id}/ai-predict`, { method: "POST" });
      if (result.error) {
        setAiError(result.error);
      } else {
        setAiPrediction(result);
      }
    } catch (e) {
      setAiError(e.toString());
    }
    setAiLoading(false);
  };

  useEffect(() => {
    loadComps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div>
        <Header />
        <div style={{ padding: 32, textAlign: "center" }}>Loading comp scoring...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Header />
        <div style={{ padding: 32, color: "red" }}>Error: {error}</div>
      </div>
    );
  }

  const target = data.target;
  const sf = target.scoring_fields;

  const formatWD = (v) => v === "in_unit" ? "Unit" : v === "in_building" ? "Bldg" : "-";

  const featureCell = (compVal, targetVal, attr, comp) => {
    const diff = comp.differences?.find((d) => d.attribute === attr);
    if (!diff) return tdStyle;
    const ded = diff.similarity_deduction;
    const bg = ded <= -20 ? "#ffcdd2" : ded <= -10 ? "#ffe0b2" : "#fff9c4";
    return { ...tdStyle, background: bg, fontWeight: 600 };
  };

  return (
    <div>
      <Header />
      <div style={{ padding: "16px 24px", maxWidth: 1400, margin: "0 auto" }}>
        {/* Target listing info */}
        <div
          style={{
            background: "#f5f5f5",
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
            border: "1px solid #ddd",
          }}
        >
          <h2 style={{ margin: "0 0 8px 0", fontSize: 18 }}>
            {target.listing_url ? (
              <a href={target.listing_url} target="_blank" rel="noreferrer" style={{ color: "#1976d2", textDecoration: "none" }}>
                {target.address?.trim()} #{target.unit_number}
              </a>
            ) : (
              <>{target.address?.trim()} #{target.unit_number}</>
            )}
            {target.listing_status && (
              <span style={{
                fontSize: 12,
                fontWeight: 500,
                marginLeft: 12,
                padding: "2px 8px",
                borderRadius: 4,
                background: target.listing_status === "ACTIVE" ? "#fff9c4"
                  : target.listing_status === "COMING SOON" ? "#e3f2fd"
                  : target.listing_status === "CONTRACT SIGNED" || target.listing_status === "CONTRACT OUT" ? "#ffe0b2"
                  : target.listing_status === "CLOSED" ? "#c8e6c9"
                  : "#e0e0e0",
              }}>
                {target.listing_status}
              </span>
            )}
          </h2>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 14 }}>
            <span><b>Price:</b> ${target.price?.toLocaleString()}</span>
            <span><b>Beds:</b> {target.bedrooms}</span>
            <span><b>Baths:</b> {target.bathrooms}</span>
            <span><b>Sqft:</b> {target.sqft?.toLocaleString()}</span>
            <span><b>Building Type:</b> {target.building_type}</span>
            <span><b>Building Tier:</b> {target.building_tier}</span>
            <span><b>Listed:</b> {target.listed_at?.slice(0, 10) || "N/A"}</span>
          </div>
          <div
            style={{
              display: "flex",
              gap: 24,
              flexWrap: "wrap",
              fontSize: 13,
              marginTop: 8,
              color: "#555",
            }}
          >
            <span>Reno: {sf.renovation ?? "N/A"}</span>
            <span>DW: {sf.dishwasher ? "Yes" : "No"}</span>
            <span>W/D: {sf.washer_dryer}</span>
            <span>Floor: {sf.floor ?? "N/A"}</span>
            <span>Outdoor: {sf.outdoor_space ? "Yes" : "No"}</span>
            <span>Views: {sf.premium_views ? "Yes" : "No"}</span>
          </div>
        </div>

        {/* AI Prediction Panel */}
        <div
          style={{
            background: aiPrediction ? "#e8f5e9" : "#fff",
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
            border: `2px solid ${aiPrediction ? "#4caf50" : "#e0e0e0"}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>AI Rent Prediction</h3>
            <button
              onClick={runAiPrediction}
              disabled={aiLoading}
              style={{
                padding: "8px 20px",
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 6,
                border: "none",
                background: aiLoading ? "#bdbdbd" : "#1976d2",
                color: "#fff",
                cursor: aiLoading ? "default" : "pointer",
              }}
            >
              {aiLoading ? "Analyzing..." : aiPrediction ? "Re-run Prediction" : "Run AI Prediction"}
            </button>
          </div>

          {aiError && (
            <div style={{ color: "#d32f2f", marginTop: 8, fontSize: 13 }}>Error: {aiError}</div>
          )}

          {aiPrediction && (
            <div style={{ marginTop: 12 }}>
              {/* Main prediction */}
              <div style={{ display: "flex", gap: 32, alignItems: "baseline", marginBottom: 12 }}>
                <div>
                  <span style={{ fontSize: 13, color: "#666" }}>Predicted Rent</span>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#2e7d32" }}>
                    ${aiPrediction.predicted_rent?.toLocaleString()}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: 13, color: "#666" }}>Range</span>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    ${aiPrediction.rent_low?.toLocaleString()} - ${aiPrediction.rent_high?.toLocaleString()}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: 13, color: "#666" }}>Confidence</span>
                  <div style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: aiPrediction.confidence === "high" ? "#2e7d32" : aiPrediction.confidence === "medium" ? "#f57c00" : "#d32f2f",
                  }}>
                    {aiPrediction.confidence?.toUpperCase()}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#999" }}>
                  Model: {aiPrediction.model} | Tokens: {aiPrediction.usage?.total_tokens}
                </div>
              </div>

              {/* Reasoning */}
              <div style={{ fontSize: 13, color: "#333", marginBottom: 12, lineHeight: 1.5 }}>
                <b>Reasoning:</b> {aiPrediction.reasoning}
              </div>

              {/* Key factors */}
              {aiPrediction.key_factors?.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                  <span style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>Key factors:</span>
                  {aiPrediction.key_factors.map((f, i) => (
                    <span key={i} style={{
                      fontSize: 11, padding: "2px 8px", borderRadius: 4,
                      background: "#e3f2fd", color: "#1565c0",
                    }}>
                      {f}
                    </span>
                  ))}
                </div>
              )}

              {/* Per-comp adjustments */}
              {aiPrediction.comp_analysis?.length > 0 && (
                <details style={{ fontSize: 12 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600, color: "#555" }}>
                    Per-comp adjustments ({aiPrediction.comp_analysis.length})
                  </summary>
                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                        <th style={{ padding: "4px 8px" }}>#</th>
                        <th style={{ padding: "4px 8px" }}>Actual Rent</th>
                        <th style={{ padding: "4px 8px" }}>Adjusted</th>
                        <th style={{ padding: "4px 8px" }}>Adjustments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiPrediction.comp_analysis.map((ca) => (
                        <tr key={ca.comp_rank} style={{ borderBottom: "1px solid #eee" }}>
                          <td style={{ padding: "4px 8px" }}>{ca.comp_rank}</td>
                          <td style={{ padding: "4px 8px" }}>${ca.rent?.toLocaleString()}</td>
                          <td style={{ padding: "4px 8px", fontWeight: 600 }}>${ca.adjusted_rent?.toLocaleString()}</td>
                          <td style={{ padding: "4px 8px", color: "#555" }}>{ca.adjustments}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Summary bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 24,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          <span>
            <b>Cumulative Score:</b>{" "}
            <span
              style={{
                color: data.reached_target ? "#2e7d32" : "#d32f2f",
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              {data.cumulative_score} / {targetScore}
            </span>
          </span>
          <span><b>Total Comps:</b> {data.total_comps}</span>
          <span><b>Same Building:</b> {data.same_building_comps}</span>
          {data.tier_summary && Object.entries(data.tier_summary).map(([key, info]) => (
            <span key={key} style={{ fontSize: 12, color: "#666" }}>
              {key.replace(/_/g, " ")}: {info.comps} ({info.score_added}pts)
            </span>
          ))}
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 12, color: "#666" }}>Target Score:</label>
            <input
              type="number"
              value={targetScore}
              onChange={(e) => setTargetScore(parseInt(e.target.value) || 700)}
              style={{ width: 60, fontSize: 12, padding: "2px 4px", borderRadius: 4, border: "1px solid #ccc" }}
              step={100}
              min={100}
            />
            <button
              onClick={refreshComps}
              disabled={refreshing}
              style={{
                padding: "4px 12px",
                fontSize: 12,
                borderRadius: 4,
                border: "1px solid #ccc",
                background: refreshing ? "#eee" : "#fff",
                cursor: refreshing ? "default" : "pointer",
              }}
            >
              {refreshing ? "Refreshing..." : "Refresh Comps"}
            </button>
          </span>
          {data.from_cache && (
            <span style={{ fontSize: 11, color: "#999" }}>
              Saved {data.comps_updated_at?.slice(0, 10)}
            </span>
          )}
        </div>

        {/* Tier legend */}
        <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 12 }}>
          {Object.entries(TIER_LABELS).map(([tier, label]) => (
            <span
              key={tier}
              style={{
                background: TIER_COLORS[tier],
                padding: "2px 8px",
                borderRadius: 4,
                border: "1px solid #ddd",
              }}
            >
              T{tier}: {label}
            </span>
          ))}
        </div>

        {/* Comps table */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 13,
          }}
        >
          <thead>
            <tr style={{ background: "#eee", textAlign: "left" }}>
              <th style={thStyle}>#</th>
              <th style={thStyle}>T</th>
              <th style={thStyle}>Address</th>
              <th style={thStyle}>Rent</th>
              <th style={thStyle}>Beds</th>
              <th style={thStyle}>Baths</th>
              <th style={thStyle}>Sqft</th>
              <th style={thStyle}>Reno</th>
              <th style={thStyle}>Floor</th>
              <th style={thStyle}>DW</th>
              <th style={thStyle}>W/D</th>
              <th style={thStyle}>Out</th>
              <th style={thStyle}>Views</th>
              <th style={thStyle}>Score</th>
              <th style={thStyle}>Dist</th>
              <th style={thStyle}>Tier</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Date</th>
              <th style={{ ...thStyle, minWidth: 200 }}>Deductions</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {/* Target reference row */}
            <tr style={{ background: "#e0e0e0", borderBottom: "2px solid #999", fontWeight: 600, fontSize: 12 }}>
              <td style={tdStyle}></td>
              <td style={tdStyle}></td>
              <td style={{ ...tdStyle, color: "#555" }}>TARGET</td>
              <td style={tdStyle}>${target.price?.toLocaleString()}</td>
              <td style={tdStyle}>{target.bedrooms}</td>
              <td style={tdStyle}>{target.bathrooms}</td>
              <td style={tdStyle}>{sf.sqft?.toLocaleString() || "-"}</td>
              <td style={tdStyle}>{sf.renovation ?? "-"}</td>
              <td style={tdStyle}>{sf.floor ?? "-"}</td>
              <td style={tdStyle}>{sf.dishwasher ? "Y" : "N"}</td>
              <td style={tdStyle}>{formatWD(sf.washer_dryer)}</td>
              <td style={tdStyle}>{sf.outdoor_space ? "Y" : "N"}</td>
              <td style={tdStyle}>{sf.premium_views ? "Y" : "-"}</td>
              <td style={tdStyle}></td>
              <td style={tdStyle}></td>
              <td style={tdStyle}>{target.building_tier}</td>
              <td style={tdStyle}></td>
              <td style={tdStyle}></td>
              <td style={tdStyle}></td>
              <td style={tdStyle}></td>
            </tr>
            {data.comps.map((comp) => {
              const runningTotal = data.comps
                .slice(0, comp.rank)
                .reduce((sum, c) => sum + c.similarity_score, 0);

              return (
                <tr
                  key={comp.listing_id}
                  style={{
                    background: comp.excluded ? "#f5f5f5" : (TIER_COLORS[comp.tier] || "#fff"),
                    borderBottom: "1px solid #ddd",
                    opacity: comp.excluded ? 0.5 : 1,
                    textDecoration: comp.excluded ? "line-through" : "none",
                  }}
                >
                  <td style={tdStyle}>{comp.rank}</td>
                  <td style={tdStyle}>{comp.tier}</td>
                  <td style={tdStyle}>
                    {comp.listing_url ? (
                      <a
                        href={comp.listing_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#1976d2", textDecoration: "none" }}
                      >
                        {comp.address?.trim()}
                      </a>
                    ) : (
                      comp.address?.trim()
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    ${comp.rent?.toLocaleString()}
                  </td>
                  <td style={tdStyle}>{comp.bedrooms}</td>
                  <td style={tdStyle}>{comp.bathrooms}</td>
                  <td style={featureCell(comp.scoring_fields?.sqft, sf.sqft, "sqft", comp)}>
                    {comp.sqft?.toLocaleString() || "-"}
                  </td>
                  <td style={featureCell(comp.scoring_fields?.renovation, sf.renovation, "renovation", comp)}>
                    {comp.scoring_fields?.renovation ?? "-"}
                  </td>
                  <td style={featureCell(comp.scoring_fields?.floor, sf.floor, "floor", comp)}>
                    {comp.scoring_fields?.floor ?? "-"}
                  </td>
                  <td style={featureCell(comp.scoring_fields?.dishwasher, sf.dishwasher, "dishwasher", comp)}>
                    {comp.scoring_fields?.dishwasher ? "Y" : "N"}
                  </td>
                  <td style={featureCell(comp.scoring_fields?.washer_dryer, sf.washer_dryer, "washer_dryer", comp)}>
                    {formatWD(comp.scoring_fields?.washer_dryer)}
                  </td>
                  <td style={featureCell(comp.scoring_fields?.outdoor_space, sf.outdoor_space, "outdoor_space", comp)}>
                    {comp.scoring_fields?.outdoor_space ? "Y" : "N"}
                  </td>
                  <td style={tdStyle}>
                    {comp.scoring_fields?.premium_views ? "Y" : "-"}
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        fontWeight: 700,
                        color:
                          comp.similarity_score >= 80
                            ? "#2e7d32"
                            : comp.similarity_score >= 50
                            ? "#f57c00"
                            : "#d32f2f",
                      }}
                    >
                      {comp.similarity_score}
                    </span>
                    <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>
                      ({runningTotal})
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {comp.is_same_building
                      ? "Same"
                      : `${comp.distance_miles}mi`}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 11 }}>{comp.building_tier}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        fontSize: 11,
                        padding: "1px 6px",
                        borderRadius: 3,
                        background:
                          comp.status === "RENTED"
                            ? "#c8e6c9"
                            : comp.status === "ACTIVE"
                            ? "#fff9c4"
                            : "#e0e0e0",
                      }}
                    >
                      {comp.status}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontSize: 11, whiteSpace: "nowrap" }}>
                    {(comp.closing_date || comp.listed_at)?.slice(0, 10) || "-"}
                  </td>
                  <td style={tdStyle}>
                    {comp.differences.length === 0 ? (
                      <span style={{ color: "#2e7d32", fontSize: 12 }}>
                        Perfect match
                      </span>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {comp.differences.map((d, i) => (
                          <span
                            key={i}
                            title={d.description}
                            style={{
                              fontSize: 11,
                              padding: "1px 6px",
                              borderRadius: 3,
                              background:
                                d.similarity_deduction <= -20
                                  ? "#ffcdd2"
                                  : d.similarity_deduction <= -10
                                  ? "#ffe0b2"
                                  : "#fff9c4",
                              cursor: "help",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {d.attribute}: {d.similarity_deduction}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => toggleExclude(comp.listing_id, comp.excluded)}
                      title={comp.excluded ? "Re-include this comp" : "Exclude this comp"}
                      style={{
                        fontSize: 10,
                        padding: "2px 6px",
                        borderRadius: 3,
                        border: "1px solid #ccc",
                        background: comp.excluded ? "#e8f5e9" : "#fff",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {comp.excluded ? "Include" : "Exclude"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {data.comps.length === 0 && (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "#999",
              fontSize: 14,
            }}
          >
            No comps found across all 4 tiers.
          </div>
        )}
      </div>
    </div>
  );
}

const thStyle = {
  padding: "8px 6px",
  borderBottom: "2px solid #ccc",
  fontSize: 12,
  fontWeight: 600,
};

const tdStyle = {
  padding: "6px",
  verticalAlign: "top",
};
